import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import type {
  MorphemeTier,
  ReferenceGraphemeMorphemeSlice,
} from "@-/dev.improve.haniverse.lib.morpheme.entities";
import type { GraphemeTaiwanUsageClassification } from "@-/dev.improve.haniverse.data.taiwan-usage-map";
import {
  MorphemeInfoPanelDictionaryGradeBadge,
  useMorphemeInfoPanelDictionaryGradeLabel,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content.views.dictionary.__sections.overview._sections.grade-badge";
import { useMemo } from "react";

const taiwanClassificationLabel: Record<GraphemeTaiwanUsageClassification, string> = {
  common: "Common",
  "less-than-common": "Uncommon",
  "rarely-used": "Rare",
};

function usageBadgeLabel({
  hanziScript,
  taiwanUsageClassification,
  tier,
}: {
  hanziScript: "simplified" | "traditional";
  taiwanUsageClassification: GraphemeTaiwanUsageClassification | null;
  tier: MorphemeTier;
}): string {
  if (hanziScript === "simplified") {
    return tier === null ? "Primitive" : `Tier ${tier}`;
  }
  return taiwanUsageClassification === null
    ? "Unlisted"
    : taiwanClassificationLabel[taiwanUsageClassification];
}

/**
 * The character surface's curriculum grade (`HSK n` / `TBCL n`) when it has one,
 * otherwise its usage (`Tier n` / Taiwan MOE classification).
 */
export function GraphemeDictionaryUsageBadge({
  card,
  taiwanUsageClassification,
}: {
  card: ReferenceGraphemeMorphemeSlice;
  taiwanUsageClassification: GraphemeTaiwanUsageClassification | null;
}) {
  const { hanziScript } = useActorUser();
  const gradeLabel = useMorphemeInfoPanelDictionaryGradeLabel(card);
  const usageLabel = useMemo(
    () => usageBadgeLabel({ hanziScript, taiwanUsageClassification, tier: card.tier }),
    [hanziScript, card.tier, taiwanUsageClassification],
  );

  return <MorphemeInfoPanelDictionaryGradeBadge label={gradeLabel ?? usageLabel} />;
}
